import { useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackConversion } from "@/utils/analytics";
import { trackClick } from "@/utils/tracker";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

export const ProductPreview = ({ product, onClose, onOrderClick, currencySymbol = "$", convertPrice, lang = "ru", t }) => {
  const displayPrice = convertPrice ? convertPrice(product.price) : product.price;
  const displayName = lang === "tr" && product.name_tr ? product.name_tr : (lang === "en" && product.name_en ? product.name_en : product.name);
  const displayDesc = lang === "tr" && product.description_tr ? product.description_tr : (lang === "en" && product.description_en ? product.description_en : product.description);
  const isPremium = product.category === "premium";
  const handleEscape = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [handleEscape]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleWhatsApp = () => {
    onOrderClick(product);
    onClose();
  };

  const handleTelegram = () => {
    const message = isPremium
      ? (lang === "tr" ? `Merhaba! "${displayName}" icin fiyat ogrenmek istiyorum` : lang === "en" ? `Hello! I'd like to know the price for "${displayName}"` : `Здравствуйте! Хочу узнать цену "${displayName}"`)
      : (t 
        ? t("wa.order", { name: displayName, price: `${currencySymbol}${displayPrice}`, city: "" }).trim()
        : `Здравствуйте! Хочу заказать "${displayName}" за ${currencySymbol}${displayPrice}`);
    trackConversion("telegram", product?.name || null);
    trackClick("telegram");
    window.open(`https://t.me/yuccaflower?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdropClick}
      data-testid="product-preview-overlay"
    >
      <div className="relative bg-white rounded-3xl overflow-hidden max-w-lg w-full max-h-[90vh] shadow-2xl animate-scale-in flex flex-col" data-testid="product-preview-modal">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-white transition-colors"
          data-testid="preview-close-btn"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>

        {/* Image */}
        <div className="w-full aspect-[3/4] sm:aspect-square overflow-hidden bg-gradient-to-br from-pink-50 to-rose-50 shrink-0">
          <img 
            src={product.image}
            alt={t ? t("alt.product", { name: displayName, city: "", price: `${currencySymbol}${displayPrice}` }) : displayName}
            className="w-full h-full object-cover"
            data-testid="preview-image"
          />
        </div>

        {/* Info */}
        <div className="flex flex-col flex-1 min-h-0">
          <div className="p-6 pb-4 space-y-4 overflow-y-auto flex-1 min-h-0">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-xl font-bold text-gray-900" data-testid="preview-product-name">{displayName}</h3>
              {isPremium ? (
                <span className="text-lg font-bold text-amber-500 shrink-0 bg-amber-50 px-3 py-1 rounded-full" data-testid="preview-product-price">
                  {lang === "tr" ? "Fiyat Sor" : lang === "en" ? "Ask Price" : "Узнать цену"}
                </span>
              ) : (
                <span className="text-2xl font-bold text-pink-500 shrink-0" data-testid="preview-product-price">{currencySymbol}{displayPrice}</span>
              )}
            </div>

            {displayDesc && (
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line" data-testid="preview-product-description">{displayDesc}</p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 p-6 pt-4 border-t border-gray-100 shrink-0 bg-white">
            <Button 
              onClick={handleWhatsApp}
              className="flex-1 bg-[#25d366] hover:bg-[#20bd5a] text-white h-12 rounded-full font-bold flex items-center justify-center gap-2 shadow-lg shadow-green-200 transition-all hover:scale-105"
              data-testid="preview-whatsapp-btn"
            >
              <WhatsAppIcon />
              <span>WhatsApp</span>
            </Button>
            <Button 
              onClick={handleTelegram}
              className="flex-1 bg-[#0088cc] hover:bg-[#0077b3] text-white h-12 rounded-full font-bold flex items-center justify-center gap-2 shadow-lg shadow-blue-200 transition-all hover:scale-105"
              data-testid="preview-telegram-btn"
            >
              <TelegramIcon />
              <span>Telegram</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPreview;
